/**
 * Техника (РС-09, РС-10, РС-16): парк машин, путевые листы по дням (маш.-ч на работах графика, простой
 * с причиной, топливо), ТО и ремонты, перемещение между проектами, отчёт по технике проекта за период;
 * показания телематики из файла системы мониторинга подставляются в путевой лист, расхождение помечается.
 */
import {
  IDLE_REASONS,
  MACHINE_OWNERSHIP,
  MACHINE_STATUSES,
  type MachineLogRow,
  type MachineReport,
  type MachineRow,
  type ResourceRow,
  seesMoney,
  type TelemetryImportResult,
  type TelemetryRow,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { LField } from '../schedule/LField';

const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');
const n1 = (x: number | null) => (x == null ? '—' : (Math.round(x * 10) / 10).toLocaleString('ru-RU'));
const num = (s: string) => {
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(v) ? null : v;
};
const monthStart = () => `${new Date().toISOString().slice(0, 7)}-01`;
const today = () => new Date().toISOString().slice(0, 10);

export function MachinesPanel({
  projectId,
  projects,
  canEdit,
}: {
  projectId: string;
  projects: Array<{ id: string; code: string }>;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['machines', projectId],
    queryFn: () => api<MachineRow[]>('/machines', { query: { projectId: projectId || undefined } }),
  });
  const [edit, setEdit] = useState<MachineRow | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['machines'] });
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Парк техники"
        meta={projectId ? 'на проекте' : 'все машины'}
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setEdit('new')} className="max-md:h-11">
              + Новая машина
            </Button>
          ) : null
        }
      >
        {q.isPending ? <Spinner /> : null}
        {q.isError ? <ErrorBox error={q.error} /> : null}
        {q.data && !q.data.length ? (
          <EmptyState title="Машин пока нет">
            Добавьте машины парка — свои, в лизинге или аренде: вид техники из справочника, моточасы, норма
            топлива и интервал ТО.
          </EmptyState>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(q.data ?? []).map((m) => (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <button
                type="button"
                onClick={() => setEdit(m)}
                className="min-h-11 min-w-0 text-left hover:underline md:min-h-0"
              >
                <span className="font-medium">
                  {m.name}
                  {m.regNumber ? ` · ${m.regNumber}` : ''}
                </span>
                <span className="block text-[12px] text-ink-2">
                  {m.resourceName} · {MACHINE_OWNERSHIP[m.ownership]}
                  {m.ownerName ? ` (${m.ownerName})` : ''}
                  {m.projectCode ? ` · ${m.projectCode}` : ' · не на проекте'}
                  {m.hourMeter != null ? ` · ${n1(m.hourMeter)} моточасов` : ''}
                </span>
              </button>
              {m.position ? (
                <a
                  href={`https://www.openstreetmap.org/?mlat=${m.position.lat}&mlon=${m.position.lon}#map=15/${m.position.lat}/${m.position.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center text-[12px] text-accent-text hover:underline md:min-h-0"
                  title="Последнее место по телематике"
                >
                  на карте · {dd(m.position.day)}
                </a>
              ) : null}
              <span className="flex items-center gap-1">
                {m.toService != null ? (
                  <Badge tone={m.toService < 0 ? 'danger' : m.toService < 25 ? 'warning' : undefined}>
                    {m.toService < 0
                      ? `ТО просрочено на ${n1(-m.toService)}`
                      : `до ТО ${n1(m.toService)} маш.-ч`}
                  </Badge>
                ) : null}
                <Badge
                  tone={m.status === 'active' ? 'success' : m.status === 'repair' ? 'danger' : undefined}
                >
                  {MACHINE_STATUSES[m.status]}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      </Panel>
      {projectId ? <LogsPanel projectId={projectId} machines={q.data ?? []} canEdit={canEdit} /> : null}
      {edit ? (
        <MachineDialog
          canEdit={canEdit}
          m={edit === 'new' ? null : edit}
          projectId={projectId}
          projects={projects}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function MachineDialog({
  canEdit,
  m,
  projectId,
  projects,
  onClose,
  onSaved,
}: {
  canEdit: boolean;
  m: MachineRow | null;
  projectId: string;
  projects: Array<{ id: string; code: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const me = useMe();
  const money = Boolean(me.tenant?.staff) && seesMoney(me.access, 'acts');
  const res = useQuery({ queryKey: ['resources'], queryFn: () => api<ResourceRow[]>('/resources') });
  const owners = useQuery({
    queryKey: ['counterparties'],
    queryFn: () => api<Array<{ id: string; name: string }>>('/counterparties'),
    enabled: canEdit,
  });
  const services = useQuery({
    queryKey: ['machine-services', m?.id],
    queryFn: () =>
      api<
        Array<{
          id: string;
          day: string;
          kind: string;
          hourMeter: number | null;
          cost: number | null;
          contractorName: string | null;
          note: string | null;
        }>
      >(`/machines/${m!.id}/services`),
    enabled: Boolean(m),
  });
  const [f, setF] = useState({
    resourceId: m?.resourceId ?? '',
    name: m?.name ?? '',
    regNumber: m?.regNumber ?? '',
    serialNumber: m?.serialNumber ?? '',
    ownership: m?.ownership ?? 'own',
    ownerId: m?.ownerId ?? '',
    projectId: m?.projectId ?? projectId,
    status: m?.status ?? 'active',
    hourMeter: m?.hourMeter != null ? String(m.hourMeter) : '',
    fuelNorm: m?.fuelNorm != null ? String(m.fuelNorm) : '',
    serviceEvery: m?.serviceEvery != null ? String(m.serviceEvery) : '',
  });
  const [svc, setSvc] = useState({ kind: 'to' as 'to' | 'repair', day: today(), hourMeter: '', cost: '' });
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });
  const body = (confirm?: boolean) => ({
    resourceId: f.resourceId,
    name: f.name,
    regNumber: f.regNumber || null,
    serialNumber: f.serialNumber || null,
    ownership: f.ownership,
    ownerId: f.ownership === 'own' ? null : f.ownerId || null,
    projectId: f.projectId || null,
    status: f.status,
    hourMeter: num(f.hourMeter),
    fuelNorm: num(f.fuelNorm),
    serviceEvery: num(f.serviceEvery),
    ...(confirm ? { confirm: true } : {}),
  });
  const save = useMutation({
    mutationFn: (confirm?: boolean) =>
      m
        ? api(`/machines/${m.id}`, { method: 'PATCH', body: body(confirm) })
        : api('/machines', { body: body() }),
    onSuccess: () => {
      toast('Сохранено');
      onSaved();
    },
    onError: (e, confirm) => {
      if (!confirm && /Подтвердите/.test(e.message) && window.confirm(e.message)) save.mutate(true);
      else toast(e.message, 'danger');
    },
  });
  const service = useMutation({
    mutationFn: () =>
      api(`/machines/${m!.id}/services`, {
        body: {
          kind: svc.kind,
          day: svc.day,
          hourMeter: num(svc.hourMeter),
          cost: money ? num(svc.cost) : null,
        },
      }),
    onSuccess: () => {
      toast(svc.kind === 'to' ? 'ТО записано' : 'Ремонт записан');
      onSaved();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const kinds = (res.data ?? []).filter((r) => r.kind === 'equipment');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title={m ? m.name : 'Новая машина'}
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!canEdit || !f.resourceId || !f.name.trim()}
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
            className="max-md:h-11"
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LField label="Вид техники (справочник)">
          <Select
            value={f.resourceId}
            onChange={(e) => set('resourceId', e.target.value)}
            className="max-md:h-11"
          >
            <option value="">—</option>
            {kinds.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Название">
          <Input value={f.name} onChange={(e) => set('name', e.target.value)} className="max-md:h-11" />
        </LField>
        <LField label="Госномер">
          <Input
            value={f.regNumber}
            onChange={(e) => set('regNumber', e.target.value)}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Заводской номер (PIN, VIN)" hint="По нему машина узнаётся в данных телематики">
          <Input
            value={f.serialNumber}
            onChange={(e) => set('serialNumber', e.target.value)}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Принадлежность">
          <Select
            value={f.ownership}
            onChange={(e) => set('ownership', e.target.value)}
            className="max-md:h-11"
          >
            {Object.entries(MACHINE_OWNERSHIP).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </LField>
        {f.ownership !== 'own' ? (
          <LField label={f.ownership === 'lease' ? 'Лизингодатель' : 'Арендодатель'}>
            <Select
              value={f.ownerId}
              onChange={(e) => set('ownerId', e.target.value)}
              className="max-md:h-11"
            >
              <option value="">—</option>
              {(owners.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </LField>
        ) : null}
        <LField label="Проект" hint={m ? 'Смена проекта — перемещение машины' : undefined}>
          <Select
            value={f.projectId}
            onChange={(e) => set('projectId', e.target.value)}
            className="max-md:h-11"
          >
            <option value="">Не на проекте</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Состояние">
          <Select value={f.status} onChange={(e) => set('status', e.target.value)} className="max-md:h-11">
            {Object.entries(MACHINE_STATUSES).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Моточасы">
          <Input
            inputMode="decimal"
            value={f.hourMeter}
            onChange={(e) => set('hourMeter', e.target.value)}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Норма топлива, л/маш.-ч">
          <Input
            inputMode="decimal"
            value={f.fuelNorm}
            onChange={(e) => set('fuelNorm', e.target.value)}
            className="max-md:h-11"
          />
        </LField>
        <LField label="ТО каждые, моточасов">
          <Input
            inputMode="decimal"
            value={f.serviceEvery}
            onChange={(e) => set('serviceEvery', e.target.value)}
            className="max-md:h-11"
          />
        </LField>
      </div>
      {m && services.data?.length ? (
        <section className="mt-4 text-[13px]">
          <h3 className="mb-1 font-semibold">ТО и ремонты</h3>
          <ul className="divide-y divide-line">
            {services.data.map((x) => (
              <li key={x.id} className="flex flex-wrap justify-between gap-2 py-1">
                <span>
                  {dd(x.day)} · {x.kind === 'to' ? 'Техобслуживание' : 'Ремонт'}
                  {x.contractorName ? ` · ${x.contractorName}` : ''}
                </span>
                <span className="num text-ink-2">
                  {x.hourMeter != null ? `${n1(x.hourMeter)} моточасов` : ''}
                  {x.cost != null ? ` · ${tenge(x.cost)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {m && canEdit ? (
        <div className="mt-4 flex flex-wrap items-end gap-2 rounded-md bg-surface-2 px-3 py-2">
          <LField label="ТО или ремонт">
            <Select
              value={svc.kind}
              onChange={(e) => setSvc({ ...svc, kind: e.target.value as 'to' | 'repair' })}
              className="max-md:h-11"
            >
              <option value="to">Техобслуживание</option>
              <option value="repair">Ремонт</option>
            </Select>
          </LField>
          <LField label="Дата">
            <Input
              type="date"
              value={svc.day}
              onChange={(e) => setSvc({ ...svc, day: e.target.value })}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Моточасы">
            <Input
              inputMode="decimal"
              value={svc.hourMeter}
              onChange={(e) => setSvc({ ...svc, hourMeter: e.target.value })}
              className="w-28 max-md:h-11"
            />
          </LField>
          {money ? (
            <LField label="Стоимость, ₸">
              <Input
                inputMode="decimal"
                value={svc.cost}
                onChange={(e) => setSvc({ ...svc, cost: e.target.value })}
                className="w-32 max-md:h-11"
              />
            </LField>
          ) : null}
          <Button loading={service.isPending} onClick={() => service.mutate()} className="max-md:h-11">
            Записать
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
}

function LogsPanel({
  projectId,
  machines,
  canEdit,
}: {
  projectId: string;
  machines: MachineRow[];
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const logs = useQuery({
    queryKey: ['machine-logs', projectId, from, to],
    queryFn: () => api<MachineLogRow[]>(`/projects/${projectId}/machine-logs`, { query: { from, to } }),
  });
  const rep = useQuery({
    queryKey: ['machine-report', projectId, from, to],
    queryFn: () => api<MachineReport>(`/projects/${projectId}/machine-report`, { query: { from, to } }),
  });
  const tel = useQuery({
    queryKey: ['telemetry', projectId, from, to],
    queryFn: () => api<TelemetryRow[]>(`/projects/${projectId}/telemetry`, { query: { from, to } }),
  });
  const [f, setF] = useState({
    machineId: '',
    day: today(),
    hours: '8',
    idleHours: '',
    idleReason: '',
    fuel: '',
    waybill: '',
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['machine-logs', projectId] });
    void qc.invalidateQueries({ queryKey: ['machine-report', projectId] });
    void qc.invalidateQueries({ queryKey: ['machines'] });
    void qc.invalidateQueries({ queryKey: ['telemetry', projectId] });
  };
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 10_000_000) throw new Error('Файл больше 10 МБ — выгрузите период короче');
      const buf = await file.arrayBuffer();
      // Таблица из Excel на русском часто в кодировке Windows-1251
      let text: string;
      try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(buf);
      } catch {
        text = new TextDecoder('windows-1251').decode(buf);
      }
      if (text.length > 5_000_000) throw new Error('Файл слишком большой — выгрузите период короче');
      return api<TelemetryImportResult>('/machines/telemetry', { body: { text } });
    },
    onSuccess: (r) => {
      toast(
        `Загружено показаний: ${r.saved}` +
          (r.unmatched.length ? `. Не найдены в парке: ${r.unmatched.slice(0, 5).join(', ')}` : '') +
          (r.skipped ? `. Пропущено строк: ${r.skipped}` : ''),
        r.unmatched.length ? 'info' : undefined,
      );
      refresh();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const telOf = (machineId: string, day: string) =>
    (tel.data ?? []).find((t) => t.machineId === machineId && t.day === day);
  const fromTel = (t: TelemetryRow) =>
    setF({
      ...f,
      machineId: t.machineId,
      day: t.day,
      hours: t.hours != null ? String(t.hours) : f.hours,
      idleHours: t.idleHours ? String(t.idleHours) : '',
      fuel: t.fuelLitres != null ? String(t.fuelLitres) : '',
    });
  const cur = f.machineId ? telOf(f.machineId, f.day) : undefined;
  const noLog = (tel.data ?? []).filter((t) => !t.hasLog && t.hours);
  const add = useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}/machine-logs`, {
        body: {
          machineId: f.machineId,
          day: f.day,
          hours: num(f.hours) ?? 0,
          idleHours: num(f.idleHours) ?? 0,
          idleReason: f.idleReason || null,
          fuelLitres: num(f.fuel),
          waybill: f.waybill || null,
        },
      }),
    onSuccess: () => {
      toast('Путевой лист записан');
      setF({ ...f, idleHours: '', idleReason: '', fuel: '', waybill: '' });
      refresh();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/machine-logs/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => toast(e.message, 'danger'),
  });
  const active = machines.filter((m) => m.status !== 'off');
  const r = rep.data;
  return (
    <>
      <Panel title="Путевые листы" meta="по дням">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <LField label="С">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="По">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="max-md:h-11" />
          </LField>
        </div>
        {canEdit && active.length ? (
          <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md bg-surface-2 px-3 py-2">
            <LField label="Машина">
              <Select
                value={f.machineId}
                onChange={(e) => setF({ ...f, machineId: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">—</option>
                {active.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </LField>
            <LField label="Дата">
              <Input
                type="date"
                value={f.day}
                onChange={(e) => setF({ ...f, day: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Работа, ч">
              <Input
                inputMode="decimal"
                value={f.hours}
                onChange={(e) => setF({ ...f, hours: e.target.value })}
                className="w-20 max-md:h-11"
              />
            </LField>
            <LField label="Простой, ч">
              <Input
                inputMode="decimal"
                value={f.idleHours}
                onChange={(e) => setF({ ...f, idleHours: e.target.value })}
                className="w-20 max-md:h-11"
              />
            </LField>
            <LField label="Причина простоя">
              <Select
                value={f.idleReason}
                onChange={(e) => setF({ ...f, idleReason: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">—</option>
                {Object.entries(IDLE_REASONS).map(([k, t]) => (
                  <option key={k} value={k}>
                    {t}
                  </option>
                ))}
              </Select>
            </LField>
            <LField label="Топливо, л">
              <Input
                inputMode="decimal"
                value={f.fuel}
                onChange={(e) => setF({ ...f, fuel: e.target.value })}
                className="w-24 max-md:h-11"
              />
            </LField>
            <LField label="№ путевого листа">
              <Input
                value={f.waybill}
                onChange={(e) => setF({ ...f, waybill: e.target.value })}
                className="w-28 max-md:h-11"
              />
            </LField>
            <Button
              disabled={!f.machineId}
              loading={add.isPending}
              onClick={() => add.mutate()}
              className="max-md:h-11"
            >
              Записать
            </Button>
            {cur ? (
              <p className="w-full text-[12px] text-ink-2">
                По телематике за этот день: {n1(cur.hours)} ч работы
                {cur.idleHours ? `, простой ${n1(cur.idleHours)} ч` : ''}
                {cur.fuelLitres != null ? `, топливо ${n1(cur.fuelLitres)} л` : ''}.{' '}
                <button
                  type="button"
                  onClick={() => fromTel(cur)}
                  className="min-h-11 text-accent-text hover:underline md:min-h-0"
                >
                  Подставить
                </button>
              </p>
            ) : null}
          </div>
        ) : null}
        {logs.isPending ? <Spinner /> : null}
        {logs.isError ? <ErrorBox error={logs.error} /> : null}
        {logs.data && !logs.data.length ? (
          <p className="text-[14px] text-ink-2">За период путевых листов нет.</p>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(logs.data ?? []).slice(0, 300).map((l) => (
            <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <span className="min-w-0">
                {dd(l.day)} · {l.machineName}
                <span className="block text-[12px] text-ink-2">
                  {n1(l.hours)} маш.-ч{l.activityCode ? ` на ${l.activityCode}` : ''}
                  {l.idleHours
                    ? ` · простой ${n1(l.idleHours)} ч: ${IDLE_REASONS[l.idleReason as keyof typeof IDLE_REASONS] ?? ''}`
                    : ''}
                  {l.fuelLitres != null ? ` · топливо ${n1(l.fuelLitres)} л` : ''}
                  {l.fuelNorm != null ? ` (норма ${n1(l.fuelNorm)})` : ''}
                  {l.waybill ? ` · ${l.waybill}` : ''}
                </span>
                {l.mismatch.length && l.telemetry ? (
                  <span className="block text-[12px] text-danger">
                    Расходится с телематикой:{' '}
                    {[
                      l.mismatch.includes('hours')
                        ? `работа ${n1(l.hours)} ч, по датчику ${n1(l.telemetry.hours)}`
                        : '',
                      l.mismatch.includes('idle')
                        ? `простой ${n1(l.idleHours)} ч, по датчику ${n1(l.telemetry.idleHours)}`
                        : '',
                      l.mismatch.includes('fuel')
                        ? `топливо ${n1(l.fuelLitres)} л, по датчику ${n1(l.telemetry.fuelLitres)}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join('; ')}
                  </span>
                ) : null}
              </span>
              {canEdit ? (
                <IconButton
                  label="Удалить путевой лист"
                  onClick={() => window.confirm('Удалить путевой лист?') && del.mutate(l.id)}
                  className="max-md:size-11"
                >
                  <Trash2 size={16} />
                </IconButton>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>
      <Panel
        title="Телематика"
        meta="моточасы, простой, топливо, пробег"
        actions={
          canEdit ? (
            <label className="inline-flex min-h-11 cursor-pointer items-center rounded-md border border-line bg-surface px-3 text-[14px] focus-within:ring-2 focus-within:ring-accent hover:bg-surface-2 md:min-h-9">
              {upload.isPending ? 'Загрузка…' : 'Загрузить файл мониторинга'}
              <input
                type="file"
                accept=".json,.csv,.txt,application/json,text/csv"
                className="sr-only"
                disabled={upload.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file);
                  e.target.value = '';
                }}
              />
            </label>
          ) : null
        }
      >
        <p className="text-[13px] text-ink-2">
          Файл системы мониторинга (Wialon и другие): снимок парка по ISO 15143-3 в JSON или таблица CSV со
          столбцами «Машина» (госномер или заводской номер), «Дата», «Моточасы» или «Часы работы», «Простой»,
          «Топливо», «Пробег», «Широта», «Долгота». Повторная загрузка дня заменяет показания.
        </p>
        {tel.isError ? <ErrorBox error={tel.error} /> : null}
        {tel.data && !tel.data.length ? (
          <p className="mt-2 text-[14px] text-ink-2">За период показаний нет.</p>
        ) : null}
        {noLog.length ? (
          <>
            <h3 className="mt-3 text-[14px] font-semibold">Есть показания, нет путевого листа</h3>
            <ul className="divide-y divide-line text-[14px]">
              {noLog.slice(0, 50).map((t) => (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                  <span>
                    {dd(t.day)} · {t.machineName}
                    <span className="block text-[12px] text-ink-2">
                      {n1(t.hours)} ч{t.idleHours ? ` · простой ${n1(t.idleHours)} ч` : ''}
                      {t.fuelLitres != null ? ` · ${n1(t.fuelLitres)} л` : ''}
                      {t.distanceKm != null ? ` · ${n1(t.distanceKm)} км` : ''}
                    </span>
                  </span>
                  {canEdit && machines.some((m) => m.id === t.machineId && m.status !== 'off') ? (
                    <Button size="sm" onClick={() => fromTel(t)} className="max-md:h-11">
                      Заполнить путевой лист
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Panel>
      {r && r.rows.length ? (
        <Panel title="Отчёт по технике" meta={`${dd(r.from)} — ${dd(r.to)}`}>
          <ScrollX>
            <table className="w-full min-w-[820px] text-[13px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2 pr-2 pl-3">Машина</th>
                  <th className="py-2 pr-2 text-right">Маш.-ч</th>
                  <th className="py-2 pr-2 text-right">По телематике</th>
                  <th className="py-2 pr-2 text-right">Простой, ч</th>
                  <th className="py-2 pr-2 text-right">Топливо: норма / факт, л</th>
                  <th className="py-2 pr-3 text-right">Стоимость смен</th>
                </tr>
              </thead>
              <tbody>
                {r.rows.map((x) => (
                  <tr key={x.machineId} className="border-t border-line">
                    <td className="py-1.5 pr-2 pl-3">
                      {x.name}
                      <div className="text-[12px] text-ink-2">{x.resourceName}</div>
                    </td>
                    <td className="num py-1.5 pr-2 text-right">{n1(x.hours)}</td>
                    <td className="num py-1.5 pr-2 text-right">
                      {n1(x.teleHours)}
                      {x.mismatchDays ? (
                        <div className="text-[12px] text-danger">расходится {x.mismatchDays} дн.</div>
                      ) : null}
                    </td>
                    <td
                      className={clsx('num py-1.5 pr-2 text-right', (x.idlePct ?? 0) > 20 && 'text-danger')}
                    >
                      {n1(x.idleHours)}
                      {x.idlePct != null ? ` (${n1(x.idlePct)} %)` : ''}
                    </td>
                    <td
                      className={clsx('num py-1.5 pr-2 text-right', (x.fuelOver ?? 0) > 0 && 'text-danger')}
                    >
                      {n1(x.fuelNorm)} / {n1(x.fuelFact)}
                    </td>
                    <td className="num py-1.5 pr-3 text-right">
                      {x.cost == null ? '—' : tenge(x.cost)}
                      {x.serviceCost ? (
                        <div className="text-[12px] text-ink-2">ТО и ремонт {tenge(x.serviceCost)}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
          {r.idle.length ? (
            <p className="mt-2 text-[13px] text-ink-2">
              Простои: {r.idle.map((x) => `${x.title} — ${n1(x.hours)} ч`).join(' · ')}
            </p>
          ) : null}
        </Panel>
      ) : null}
    </>
  );
}
